<template>
  <StickyFooter :sticky="!!canContinue">
    <template #header>
      <section class="hero is-info">
        <div class="loader-hero-body">
          <span class="loader-hero-title">
            Fill up assay details ({{ $filters.lowercase(assayType) }})
          </span>
        </div>
      </section>
    </template>
    <template #body>
      <AssayView
        v-if="dv.isRecordReady && assayType"
        :key="`new-assay-${assayType}`"
        class="detail-container padded-container mt-3 mb-5"
        :dv="dv"
      />
    </template>
    <template #footer>
      <BaseButton
        :key="`head-continue-${canContinue}`"
        variant="primary"
        :outlined="!canContinue"
        :disabled="!canContinue || isContinuing"
        :loading="isContinuing"
        :icon="['fa', 'fa-step-forward']"
        label="Continue"
        @click="handleContinue"
      />
    </template>
  </StickyFooter>
</template>

<script>
import { ref, computed, provide, watch, toRaw } from 'vue'
import { useRouter } from 'vue-router'
import { cloneDeep } from 'lodash-es'
import { useStore } from '@/store'

import AssayView from '@/components/detail/assay/AssayView.vue'
import StickyFooter from '@/components/data-loader/StickyFooter.vue'

import { useNewView } from '@/composables/useNewView'
import { put } from '@/api'

export default {
  name: 'WizardAssayNewView',
  components: {
    StickyFooter,
    AssayView,
  },
  setup(_, { attrs }) {
    const store = useStore()
    const router = useRouter()
    const isContinuing = ref(false)

    // Read assay type and template data from Vuex store
    const assayType = computed(
      () => store.getters['dataloader/assay/resolvedAssayType'],
    )
    const templateData = computed(
      () => store.getters['dataloader/assay/transferableTemplateData'],
    )
    const existingAssayData = computed(
      () => store.state.dataloader.assay.assayData,
    )
    const existingAssayId = computed(() => existingAssayData.value?.id?.value)

    // Create dv via useNewView
    const dv = useNewView({
      appName: 'assays',
      modelName: 'assays',
      modelType: assayType,
      initialRecord: () => {
        // Navigate-back is handled by the watch below
        if (existingAssayId.value) return null
        // New assay from template: use template data as base record
        const tpl = templateData.value
        return tpl && Object.keys(tpl).length > 0 ? tpl : null
      },
    })

    // Navigate-back: once the meta-default record is ready, replace it
    // with the full saved record from the store. Using a separate watch
    // avoids timing dependencies between meta resolution and store data.
    // Declare stopRestore before the watch so it's available inside the
    // callback when { immediate: true } fires synchronously.
    let stopRestore
    stopRestore = watch(
      () => dv.isRecordReady && existingAssayId.value,
      (shouldRestore) => {
        if (!shouldRestore) return
        const storeData = toRaw(existingAssayData.value)
        if (!storeData || Object.keys(storeData).length === 0) return
        dv.record = { ...dv.record, ...cloneDeep(storeData) }
        stopRestore?.()
      },
      { immediate: true },
    )

    // Tell child components this is a "new" record context
    provide('fieldPage', 'new')

    // Validation
    const canContinue = computed(() => !dv.hasValidationErrors)

    // Save logic
    async function handleContinue() {
      if (isContinuing.value) return
      isContinuing.value = true
      try {
        if (existingAssayId.value) {
          // Update existing assay
          const payload = { data: { results: dv.record } }
          const response = await put(
            `assays/assays/${existingAssayId.value}/`,
            payload,
          )
          store.commit('dataloader/assay/SET_ASSAY_DATA', response.data.results)
        } else {
          // Create new assay
          const res = await dv.createRecord()
          const assayId = res.id.value
          await store.dispatch('dataloader/assay/setAssayData', assayId)
        }
        router.push(attrs.nextRoute)
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'error'
        store.commit('notify', {
          type: 'error',
          message: errorMessage,
        })
      } finally {
        isContinuing.value = false
      }
    }

    return {
      dv,
      assayType,
      canContinue,
      isContinuing,
      handleContinue,
    }
  },
}
</script>
